import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Home, Brain, Trophy, Zap, TrendingUp, Users, Target, Lightbulb, 
  CheckCircle2, Sparkles, ArrowRight, BookOpen, Clock, Send, StickyNote,
  Table, FileText, Grid3x3, Timer, ChevronRight, PlayCircle, Plus
} from 'lucide-react';
import Tooltip from '../components/Tooltip';
import AppFooter from '../components/AppFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FeatureGallery = () => {
  const navigate = useNavigate();
  const [userMetrics, setUserMetrics] = useState(null);
  const [userSessions, setUserSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sample fallback data
  const sampleMetrics = {
    totalSessions: 24,
    completedSessions: 18,
    avgOI: 8.4,
    avgSpark: 78
  };

  const sampleIdeaCards = [
    {
      id: '1',
      title: 'AI-Powered Urban Vertical Farming System',
      description: 'Automated hydroponic systems using ML for optimized nutrient delivery',
      oi_total: 8.7,
      spark_pct: 80,
      status: 'completed',
      date: '2025-01-15'
    },
    {
      id: '2',
      title: 'Blockchain-Based Supply Chain Transparency',
      description: 'Decentralized platform for tracking product origins and ethical sourcing',
      oi_total: 7.9,
      spark_pct: 65,
      status: 'in-progress',
      date: '2025-01-18'
    },
    {
      id: '3',
      title: 'Community-Driven Renewable Energy Grid',
      description: 'Peer-to-peer energy sharing network powered by solar and wind',
      oi_total: 8.4,
      spark_pct: 72,
      status: 'completed',
      date: '2025-01-20'
    }
  ];

  const sampleProjections = {
    opportunities: [
      "Smart agriculture market projected to reach $34B by 2026",
      "Growing urban food security concerns in 600+ cities globally",
      "Government incentives for sustainable farming tech increasing",
      "Climate resilience as top priority for 80% of municipalities"
    ],
    precedents: [
      "Plenty (SF) - raised $500M, vertical farming at scale",
      "AeroFarms - NASA collaboration on controlled environment agriculture",
      "80 Acres Farms - automation achieving 300x productivity vs traditional",
      "Bowery Farming - ML optimization reducing resource use by 95%"
    ],
    valuation: [
      "Initial MVP: $500K - $1M (angel/seed)",
      "With pilot deployments: $3M - $5M (Series A potential)",
      "Scaled operations (5+ cities): $15M - $25M valuation range",
      "Market leader position: $100M+ potential (AgTech unicorn path)"
    ],
    nextSteps: [
      "Week 1-2: Prototype sensor integration & ML model training",
      "Week 3-4: Pilot deployment with 2-3 urban community gardens",
      "Month 2: Partnership discussions with municipal sustainability offices",
      "Month 3: Seed funding round + scale to 10 locations"
    ]
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      // Try to fetch real user data
      const metricsResponse = await fetch(`${BACKEND_URL}/api/metrics/user-dashboard`, {
        credentials: 'include'
      });

      const sessionsResponse = await fetch(`${BACKEND_URL}/api/sessions`, {
        credentials: 'include'
      });

      if (metricsResponse.ok && sessionsResponse.ok) {
        const metrics = await metricsResponse.json();
        const sessions = await sessionsResponse.json();
        
        setUserMetrics(metrics);
        setUserSessions(sessions.slice(0, 3)); // Top 3 sessions
      }
    } catch (error) {
      console.log('Using sample data (user not authenticated or error occurred)');
    } finally {
      setLoading(false);
    }
  };

  const displayMetrics = sampleMetrics; // Always show sample data for showcase
  const displayIdeaCards = sampleIdeaCards; // Always show sample cards for showcase

  const getScoreBadgeColor = (score) => {
    if (score >= 8.5) return 'bg-purple-600';
    if (score >= 7) return 'bg-green-600';
    if (score >= 5) return 'bg-blue-600';
    return 'bg-orange-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
                Feature Gallery
              </h1>
              <p className="text-sm text-slate-600">Explore Idea Fountain's powerful features</p>
            </div>
          </div>
          <Tooltip content="Return to homepage">
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </Tooltip>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-7xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Your Ideation Command Center
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From initial spark to fully-formed innovation - track, analyze, and accelerate your ideas with AI-powered insights
          </p>
        </div>

        {/* User Metrics Dashboard */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-600" />
            Innovation Metrics Dashboard
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Demo Data</Badge>
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white border-0">
              <CardContent className="pt-6">
                <Trophy className="w-8 h-8 mb-3 opacity-90" />
                <div className="text-3xl font-bold mb-1">{displayMetrics?.totalSessions || 0}</div>
                <div className="text-sm opacity-90">Total Sessions</div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-500 to-cyan-500 text-white border-0">
              <CardContent className="pt-6">
                <CheckCircle2 className="w-8 h-8 mb-3 opacity-90" />
                <div className="text-3xl font-bold mb-1">{displayMetrics?.completedSessions || 0}</div>
                <div className="text-sm opacity-90">Completed</div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-500 to-indigo-500 text-white border-0">
              <CardContent className="pt-6">
                <Brain className="w-8 h-8 mb-3 opacity-90" />
                <div className="text-3xl font-bold mb-1">{displayMetrics?.avgOI?.toFixed(1) || '0.0'}</div>
                <div className="text-sm opacity-90">Avg OI Score</div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white border-0">
              <CardContent className="pt-6">
                <Zap className="w-8 h-8 mb-3 opacity-90" />
                <div className="text-3xl font-bold mb-1">{displayMetrics?.avgSpark || 0}%</div>
                <div className="text-sm opacity-90">Avg Spark</div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Idea Cards Showcase */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-purple-600" />
            Idea Portfolio Examples
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Demo Data</Badge>
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {displayIdeaCards.map((idea) => (
              <Card key={idea.id} className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-purple-200">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge className={`${getScoreBadgeColor(idea.oi_total || idea.oi?.total)} text-white`}>
                      OI: {(idea.oi_total || idea.oi?.total)?.toFixed(1)}/10
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {idea.status || 'completed'}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg">{idea.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-3">
                    {idea.description || idea.content?.substring(0, 120) + '...'}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      {idea.spark_pct || idea.msa?.spark_pct}% Spark
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(idea.date || idea.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* IF Projections Showcase */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Target className="w-6 h-6 text-purple-600" />
            AI-Powered IF Projections
            <Badge variant="outline" className="text-xs">Sample for "AI Urban Farming"</Badge>
          </h3>
          <p className="text-slate-600 mb-6">
            Get strategic insights to transform your ideas into reality - opportunity analysis, competitive landscape, valuation guidance, and actionable next steps.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Opportunity Areas */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Target className="w-5 h-5" />
                  Opportunity Areas
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {sampleProjections.opportunities.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Precedents */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <BookOpen className="w-5 h-5" />
                  Notable Precedents
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {sampleProjections.precedents.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Valuation Insights */}
            <Card className="border-2 border-emerald-200">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50">
                <CardTitle className="flex items-center gap-2 text-emerald-900">
                  <TrendingUp className="w-5 h-5" />
                  Valuation Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {sampleProjections.valuation.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <Trophy className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Next Steps */}
            <Card className="border-2 border-orange-200">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50">
                <CardTitle className="flex items-center gap-2 text-orange-900">
                  <ArrowRight className="w-5 h-5" />
                  Strategic Next Steps
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {sampleProjections.nextSteps.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <ArrowRight className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Think Lab Power Features */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Brain className="w-6 h-6 text-purple-600" />
            Think Lab - Your AI-Powered Workspace
          </h3>
          <p className="text-slate-600 mb-6">
            A comprehensive toolkit for structured thinking, research, and project management - all integrated with AI assistance.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Brainstorm */}
            <Card className="border-2 border-purple-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                  <Send className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">AI Brainstorm</h4>
                <p className="text-sm text-slate-600">
                  Chat with AI for ideation, refine concepts, and explore possibilities in real-time
                </p>
              </CardContent>
            </Card>

            {/* Resources */}
            <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Auto Resources</h4>
                <p className="text-sm text-slate-600">
                  Curated articles, papers, and references automatically matched to your session
                </p>
              </CardContent>
            </Card>

            {/* Timer */}
            <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Smart Timer</h4>
                <p className="text-sm text-slate-600">
                  Track thinking sessions with focus modes and productivity insights
                </p>
              </CardContent>
            </Card>

            {/* Idea Board */}
            <Card className="border-2 border-yellow-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center mb-4">
                  <StickyNote className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Idea Board</h4>
                <p className="text-sm text-slate-600">
                  Visual workspace to capture, organize, and connect your thoughts
                </p>
              </CardContent>
            </Card>

            {/* Think Project */}
            <Card className="border-2 border-indigo-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mb-4">
                  <Table className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Think Project</h4>
                <p className="text-sm text-slate-600">
                  Task management with Kanban boards, sprints, and AI-generated action items
                </p>
              </CardContent>
            </Card>

            {/* Design Sprint */}
            <Card className="border-2 border-pink-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center mb-4">
                  <Timer className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Design Sprint</h4>
                <p className="text-sm text-slate-600">
                  Google's 5-day design thinking framework with guided workflows
                </p>
              </CardContent>
            </Card>

            {/* Notice Board */}
            <Card className="border-2 border-cyan-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Notice Board</h4>
                <p className="text-sm text-slate-600">
                  Pin important links with AI-generated summaries for quick reference
                </p>
              </CardContent>
            </Card>

            {/* Stakeholder Map */}
            <Card className="border-2 border-emerald-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Who For?</h4>
                <p className="text-sm text-slate-600">
                  Map stakeholders, understand users, and analyze impact on different groups
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* IF Session Flow */}
        <section className="mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <PlayCircle className="w-6 h-6 text-purple-600" />
            How an IF Session Works - Step by Step
          </h3>
          <p className="text-slate-600 mb-8">
            From initial spark to fully-formed innovation with AI guidance at every step
          </p>

          <div className="space-y-4">
            {/* Step 1 */}
            <Card className="border-l-4 border-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-slate-900 mb-2">Choose Your Persona</h4>
                    <p className="text-slate-600 mb-3">
                      Select from 8 specialized personas (Innovator, Researcher, Designer, etc.) that shape your ideation journey with tailored prompts and frameworks
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">8 Personas</Badge>
                      <Badge variant="outline" className="text-xs">Adaptive Prompts</Badge>
                      <Badge variant="outline" className="text-xs">Contextual Guidance</Badge>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            {/* Step 2 */}
            <Card className="border-l-4 border-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-slate-900 mb-2">Brain Dump Your Idea</h4>
                    <p className="text-slate-600 mb-3">
                      Start with a conversational intro prompt, then pour out your thoughts. Upload documents, paste research, or start from scratch - AI tracks everything
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">Document Upload</Badge>
                      <Badge variant="outline" className="text-xs">Context Tracking</Badge>
                      <Badge variant="outline" className="text-xs">Dynamic Prompts</Badge>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            {/* Step 3 */}
            <Card className="border-l-4 border-green-500">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-slate-900 mb-2">AI-Powered Deep Analysis</h4>
                    <p className="text-slate-600 mb-3">
                      GPT-5 analyzes your idea across 4 dimensions: Novelty (30%), Resonance (30%), Synthesis (20%), Ethical Anchoring (20%) with detailed justifications
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">OI Scoring</Badge>
                      <Badge variant="outline" className="text-xs">MSA Assessment</Badge>
                      <Badge variant="outline" className="text-xs">Detailed Feedback</Badge>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            {/* Step 4 */}
            <Card className="border-l-4 border-orange-500">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold">
                    4
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-slate-900 mb-2">Get Strategic IF Projections</h4>
                    <p className="text-slate-600 mb-3">
                      AI generates opportunity analysis, competitive precedents, valuation insights, and concrete next steps - your roadmap to implementation
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">Market Opportunities</Badge>
                      <Badge variant="outline" className="text-xs">Precedent Analysis</Badge>
                      <Badge variant="outline" className="text-xs">Valuation Guide</Badge>
                      <Badge variant="outline" className="text-xs">Action Plan</Badge>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            {/* Step 5 */}
            <Card className="border-l-4 border-emerald-500">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center text-white font-bold">
                    5
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg text-slate-900 mb-2">Refine with Think Lab</h4>
                    <p className="text-slate-600 mb-3">
                      Open Think Lab to brainstorm further, manage tasks, run design sprints, track stakeholders, and transform your idea into reality
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">8 Power Tools</Badge>
                      <Badge variant="outline" className="text-xs">AI Integration</Badge>
                      <Badge variant="outline" className="text-xs">Project Management</Badge>
                    </div>
                  </div>
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Fountainhead Community Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
              Fountainhead Community
            </h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Connect, compete, and grow with a global network of innovators
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Public Profiles */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-purple-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Fountainhead Profiles</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Build your unique profile with expertise, passions, mission statement, and social links. Showcase your innovation journey.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                    <span>Custom username & bio</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                    <span>LinkedIn & website links</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                    <span>IQI, OI, MSA stats display</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Follow System */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-pink-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Follow & Connect</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Follow innovative Fountainheads, track their creative journey, and get inspired by breakthrough ideas.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-pink-600" />
                    <span>Follow/unfollow users</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-pink-600" />
                    <span>View followers & following</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-pink-600" />
                    <span>Activity notifications</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Leaderboards */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-orange-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center mb-3">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Leaderboards</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Compete on IQI, OI, and MSA rankings. Earn "Empathetic Innovator" title and inspire the community.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-orange-600" />
                    <span>IQI leaderboard (Empathetic Innovators)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-orange-600" />
                    <span>OI leaderboard (Original Thinkers)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-orange-600" />
                    <span>Time filters: Week, Month, All-time</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-l-4 border-purple-500">
            <div className="flex items-start gap-4">
              <Sparkles className="w-8 h-8 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-lg text-purple-900 mb-2">Everyone's a Fountainhead</h4>
                <p className="text-gray-700 mb-3">
                  In our community, every innovator is a Fountainhead—a source of original ideas and creative energy. 
                  Your IQI (Interaction Quality Index) is always public to maintain quality interactions, while you control 
                  who sees your OI and MSA scores.
                </p>
                <p className="text-sm text-gray-600 italic">
                  Inspired by Ayn Rand's "The Fountainhead" — celebrating individualism, creativity, and the power of original thinking.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Social Innovation Challenges Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Social Innovation Challenges
            </h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Tackle real-world problems aligned with UN Sustainable Development Goals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* UN SDG Challenges */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-green-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mb-3">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <CardTitle>UN SDG Challenges</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Engage with challenges addressing poverty, hunger, health, education, climate action, and all 17 UN Sustainable Development Goals.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>17 SDG categories</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Global impact focus</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Evidence-based solutions</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Challenge Types */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-emerald-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center mb-3">
                  <Grid3x3 className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Regional & Global Challenges</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Choose challenges at different scales - local community issues, regional problems, or global crises requiring innovative solutions.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Regional challenges</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>National initiatives</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Global impact goals</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Challenge Discussions */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-teal-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Discussion Forums</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Collaborate with other innovators through challenge-specific discussion boards, share insights, and refine solutions together.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-teal-600" />
                    <span>Public & team discussions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-teal-600" />
                    <span>Threaded conversations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-teal-600" />
                    <span>Community moderation</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submission Gallery */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-cyan-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center mb-3">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Public Submission Gallery</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Showcase your solutions in a public gallery, gain recognition, and inspire others with your innovative approaches.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                    <span>OI & MSA scoring</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                    <span>Likes & engagement</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                    <span>Leaderboard rankings</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Create Your Own */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-blue-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mb-3">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Create Custom Challenges</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Design your own challenges for your organization, community, or special interest area with custom criteria and goals.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span>Custom challenge creation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span>Set difficulty & duration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span>Team invitations</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Impact Lab Integration */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-indigo-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mb-3">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Launch Impact Labs</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Transform any challenge into a structured 7-day Impact Lab sprint with IDEO methodology and team collaboration.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                    <span>One-click Impact Lab</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                    <span>AI-powered analysis</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                    <span>Team-based sprints</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-l-4 border-green-500">
            <div className="flex items-start gap-4">
              <Target className="w-8 h-8 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-lg text-green-900 mb-2">Social Innovation Platform</h4>
                <p className="text-gray-700">
                  Join Social Innovation Challenges to tackle real-world problems, collaborate with global innovators, and create solutions that make a meaningful impact. 
                  From local community challenges to global SDG goals, every submission is scored with our rigorous OI and MSA framework.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Lab - IDEO Methodology Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent">
              Impact Lab - IDEO Design Thinking
            </h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              7-day structured innovation sprints powered by IDEO's proven design thinking methodology
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* IDEO Methodology */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-emerald-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center mb-3">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <CardTitle>IDEO 7-Step Process</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Follow the world-renowned IDEO design thinking framework: Frame, Inspire, Synthesize, Ideate, Prototype, Test, and Share.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Structured 7-day roadmap</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Daily objectives & activities</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Clear deliverables each day</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI-Powered Research */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-teal-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mb-3">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <CardTitle>GPT-5 Analysis & Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Comprehensive AI-powered analysis with secondary research, precedents, solution frameworks, and curated resources.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-teal-600" />
                    <span>Real precedents & case studies</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-teal-600" />
                    <span>8-12 curated resources</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-teal-600" />
                    <span>Think Lab exercises</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team Collaboration */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-cyan-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Team Collaboration (6-12 Members)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Invite diverse team members with recommended personas and expertise to tackle complex challenges together.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                    <span>Team invitations & management</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                    <span>Suggested team personas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                    <span>Daily progress tracking</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Certificate & Export */}
            <Card className="hover:shadow-xl transition-shadow border-2 border-blue-100">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mb-3">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <CardTitle>Export & Certification</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Download your comprehensive analysis and receive certificates upon completion of your 7-day innovation sprint.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span>Markdown export</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span>Completion certificate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span>Impact story documentation</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border-l-4 border-emerald-500">
            <div className="flex items-start gap-4">
              <Sparkles className="w-8 h-8 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-lg text-emerald-900 mb-2">IDEO-Inspired Virtual Innovation Space</h4>
                <p className="text-gray-700">
                  Impact Lab brings IDEO's world-class design thinking methodology to your challenge. Each sprint follows a proven 
                  7-step process with AI-powered research, expert team guidance, and structured activities that lead to breakthrough innovations. 
                  Perfect for tackling complex social, environmental, and technological challenges.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-4">Ready to Start Your Innovation Journey?</h3>
          <p className="text-lg mb-8 opacity-90">
            Join thousands of innovators using Idea Fountain to transform concepts into reality
          </p>
          <div className="flex gap-4 justify-center">
            <Tooltip content="Start a new thinking session">
              <Button 
                size="lg" 
                className="bg-white text-purple-600 hover:bg-slate-100"
                onClick={() => navigate('/session/new')}
              >
                <Brain className="w-5 h-5 mr-2" />
                Start New Session
              </Button>
            </Tooltip>
            <Tooltip content="Evaluate your research paper">
              <Button 
                size="lg" 
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white/10"
                onClick={() => navigate('/quick-evaluate')}
              >
                <Trophy className="w-5 h-5 mr-2" />
                Quick Evaluate
              </Button>
            </Tooltip>
          </div>
        </section>
      </div>
      
      <AppFooter />
    </div>
  );
};

export default FeatureGallery;
